import { type FC, type MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeProps } from '@kizen/kds/Badge';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import ActionCell from '__components/Tables/ActionCell';
import { Link, useHistory } from 'react-router-dom';
import {
  BUSINESS_APP_ACTIONS,
  EMPTY_VALUE,
} from 'ts-components/Plugins/businessApps/constants';
import { useCurrentUserId } from 'hooks/useCurrentUserId';
import { kiCodeCopy } from 'ts-components/KiCode/copy';
import { businessAppDetailsUrl } from 'pages/BusinessApps/constants';
import {
  type AppNotice,
  type BusinessAppAction,
  type BusinessAppPermissions,
  type BusinessAppTableRow,
  appNotices,
  formatDateTime,
  getVersionActions,
  otherVersions,
  publishedByOf,
  versionLabel,
  versionStatus,
} from 'ts-components/Plugins/businessApps/helpers';
import type { BusinessAppStatus } from 'ts-components/Plugins/businessApps/types';

export const STATUS_BADGE_PROPS: Record<
  BusinessAppStatus,
  Pick<BadgeProps, 'color' | 'bgColor'>
> = {
  live: {
    color: 'feedback-success-foreground',
    bgColor: 'feedback-success-bg',
  },
  preview: { color: 'feedback-info-foreground', bgColor: 'feedback-info-bg' },
  disabled: { color: 'font-secondary', bgColor: 'background-light' },
};

export const useStatusLabels = () => {
  const { t } = useTranslation();
  return useMemo(
    (): Record<BusinessAppStatus, string> => ({
      live: t('Published'),
      preview: t('Preview'),
      disabled: t('Disabled'),
    }),
    [t]
  );
};

export const useActionLabels = () => {
  const { t } = useTranslation();
  return useMemo(
    (): Record<BusinessAppAction, string> => ({
      view: t('View Details'),
      edit_with_kicode: kiCodeCopy(t).editWithKiCode,
      use_version: t('Switch to This Version'),
      edit_configuration: t('Edit Configuration'),
      publish_preview: t('Publish'),
      delete_preview: t('Delete Preview'),
      delete_app: t('Delete App'),
      delete_version: t('Delete Version'),
      disable: t('Disable'),
      enable: t('Enable'),
    }),
    [t]
  );
};

const ActionsCell: FC<{
  data?: BusinessAppTableRow;
  permissions: BusinessAppPermissions;
  onSelectAction: (
    action: { value: BusinessAppAction },
    row: BusinessAppTableRow
  ) => void;
}> = ({ data, permissions, onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const labels = useActionLabels();
  const currentUserId = useCurrentUserId();
  const options = useMemo(
    () =>
      data?.version
        ? getVersionActions(
            data.app,
            data.version,
            permissions,
            currentUserId,
            { includeKiCode: data.kind === 'app' }
          ).map((value) => ({
            value,
            label:
              value === BUSINESS_APP_ACTIONS.useVersion &&
              data.version?.status !== 'preview'
                ? t('Switch to Published Version')
                : labels[value],
          }))
        : [],
    [data, permissions, currentUserId, labels, t]
  );

  if (!options.length) {
    return <SizedCell width="50px" padding="15px" />;
  }

  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

export const AppNotificationIcon: FC<{
  detailsUrlFor: (subpath: string) => string;
  notices: AppNotice[];
  severity: 'error' | 'warning';
  qaId: string;
}> = ({ detailsUrlFor, notices, severity, qaId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  if (!notices.length) {
    return null;
  }
  const to = detailsUrlFor(notices[0].subpath);
  return (
    <Link
      to={to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
        history.push(to, { from: history.location.pathname });
      }}
      data-qa-id={qaId}
    >
      <Icon
        icon="message-warning-triangle"
        color={severity === 'error' ? 'feedback-error' : 'feedback-warning'}
        tooltipDescriptionOverride={[
          ...notices.map(({ message }) => message),
          t('Click to view details.'),
        ].join(' ')}
        tooltipEnabled
      />
    </Link>
  );
};

type UseColumnsProps = {
  permissions: BusinessAppPermissions;
  errorCounts: Record<string, number>;
  missingAppSettings: Record<string, number>;
  missingUserSettings: Record<string, number>;
  onToggleExpanded: (apiName: string) => void;
  onOpenDetails: (apiName: string, previewOwnerId?: string | null) => void;
  onSelectAction: (
    action: { value: BusinessAppAction },
    row: BusinessAppTableRow
  ) => void;
};

export const useColumns = ({
  permissions,
  errorCounts,
  missingAppSettings,
  missingUserSettings,
  onToggleExpanded,
  onOpenDetails,
  onSelectAction,
}: UseColumnsProps) => {
  const { t } = useTranslation();
  const statusLabels = useStatusLabels();

  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="45px" />,
        cell: <SizedCell width="45px" />,
        format: () => <span />,
      },
      {
        id: 'name',
        headCell: (
          <SortableHeadCell label={t('Name')} width="240px" padding="30px" />
        ),
        cell: <SizedCell width="240px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => {
          if (row.kind === 'version') {
            return <span />;
          }
          const previewOwnerId =
            row.version?.status === 'preview'
              ? row.version.preview_owner?.id
              : null;
          return (
            <span className="flex items-center gap-[10px] min-w-0 -ml-[30px]">
              {otherVersions(row.app).length ? (
                <Button
                  variant="text"
                  color="tertiary"
                  rightIcon={row.expanded ? 'table-collapse' : 'table-expand'}
                  aria-label={row.expanded ? t('Collapse') : t('Expand')}
                  onClick={() => onToggleExpanded(row.app.api_name)}
                  qa={{ id: `business-app-expand-${row.app.api_name}` }}
                />
              ) : (
                <span className="w-[20px] flex-none" />
              )}
              <TextEllipsisWithTooltip
                type="anchor"
                href={businessAppDetailsUrl(row.app.api_name, previewOwnerId)}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  onOpenDetails(row.app.api_name, previewOwnerId);
                }}
                data-qa-id={`business-app-name-${row.id}`}
              >
                {row.app.name}
              </TextEllipsisWithTooltip>
            </span>
          );
        },
      },
      {
        id: 'stage',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Stage')}
            width="170px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="170px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => (
          <span className="flex items-center gap-[10px]">
            <TextEllipsisWithTooltip>
              {row.version?.status === 'preview'
                ? statusLabels.preview
                : statusLabels.live}
            </TextEllipsisWithTooltip>
            {row.version?.is_current_for_viewer ? (
              <Badge label={t('Viewing')} {...STATUS_BADGE_PROPS.disabled} />
            ) : null}
          </span>
        ),
      },
      {
        id: 'status',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Status')}
            width="130px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="130px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => {
          const status = versionStatus(row.app, row.version);
          const previewOwnerId =
            row.version?.status === 'preview'
              ? row.version.preview_owner?.id
              : null;
          return (
            <span className="flex items-center gap-[10px]">
              {status === 'preview' ? (
                <Typography color="font-placeholder">{EMPTY_VALUE}</Typography>
              ) : (
                <Typography
                  color={
                    status === 'disabled'
                      ? 'font-secondary'
                      : 'feedback-success'
                  }
                >
                  {status === 'disabled' ? t('Disabled') : t('Enabled')}
                </Typography>
              )}
              {row.kind === 'app' ? (
                <AppNotificationIcon
                  detailsUrlFor={(subpath) =>
                    businessAppDetailsUrl(
                      row.app.api_name,
                      previewOwnerId,
                      subpath
                    )
                  }
                  notices={appNotices(
                    t,
                    errorCounts[row.app.api_name],
                    missingAppSettings[row.app.api_name],
                    missingUserSettings[row.app.api_name]
                  )}
                  severity={errorCounts[row.app.api_name] ? 'error' : 'warning'}
                  qaId={`business-app-notifications-${row.app.api_name}`}
                />
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'version',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Version')}
            width="100px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="100px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => (
          <TextEllipsisWithTooltip>
            {versionLabel(row.version) || EMPTY_VALUE}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'updated',
        headCell: (
          <SortableHeadCell
            label={t('Date Updated')}
            numeric
            width="160px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="160px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => (
          <TextEllipsisWithTooltip>
            {formatDateTime(t, row.version?.updated ?? row.app.updated)}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'published_by',
        headCell: (
          <SortableHeadCell
            label={t('Updated By')}
            width="180px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="180px" padding="30px" />,
        format: (_: unknown, row: BusinessAppTableRow) => {
          const publishedBy = publishedByOf(row.app, row.version);
          return (
            <TextEllipsisWithTooltip>
              {publishedBy?.full_name || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="110px"
            padding="15px"
            align="right"
          />
        ),
        cell: (
          <ActionsCell
            permissions={permissions}
            onSelectAction={onSelectAction}
            data-qa-id="business-app-row-actions"
          />
        ),
      },
    ],
    [
      t,
      statusLabels,
      permissions,
      errorCounts,
      missingAppSettings,
      missingUserSettings,
      onToggleExpanded,
      onOpenDetails,
      onSelectAction,
    ]
  );
};
